/**
 * Locations data-access adapter — the client-wrapper boundary.
 *
 * Maps the contract `Location` DTO (@v0y4ge/api-contracts) → the UI row shape
 * the Studio Locations screen renders. UI stays verbatim; every contract↔UI
 * divergence lives here.
 *
 * SHAPE RECONCILIATION (flagged for Claude Code — see CONTRACT-ASKS):
 *  - tz ← timezone (rename only).
 *  - hours: NOW in the contract (LocationHours = [{ day, windows:[{open,close}] }]),
 *    carried through verbatim; the editor maps it to/from its F-98 weekly grid.
 *  - products[] (appointments/events/queues): NOT a stored field — derived
 *    client-side from what's scheduled at the location (editor concern; list
 *    needs none). image: pending — CD picks `imageAssetId` (canvas-assets ref);
 *    client-local until the editor's asset-upload flow is wired.
 *  - capacity exists in the contract but F-105 removed it from the UI; kept on
 *    `raw` for any consumer that still wants it.
 */

import type { Location as LocationDto } from "@v0y4ge/api-contracts";
import { t as i18nT } from "../../lib/i18n";

export type UILocationStatus = "active" | "inactive" | "archived";

/** Weekly hours grid (contract LocationHours). Declared locally so the adapter
 * compiles against kit contract versions that predate the `hours` field; read
 * defensively from the DTO. */
export interface LocationHoursWindow { open: string; close: string }
export interface LocationDayHours { day: string; windows: LocationHoursWindow[] }
export type LocationHours = LocationDayHours[];

export interface UILocation {
  id: string;
  name: string;
  /** One-line composed address (street · city, region postal · country). */
  address: string;
  city: string;
  tz: string;
  status: UILocationStatus;
  /** Count of events that have used this location (derived, read-only). */
  eventsCount: number;
  /** Count of rooms/resources based here (derived, read-only). */
  roomsCount: number;
  /** Count of staff with availability/assignment at this location. */
  staffCount: number;
  /** Capacity; undefined when not set. */
  capacity: number | undefined;
  publicHub: LocationDto["publicHub"];
  amenities: string[];
  active: boolean;
  archived: boolean;
  /** Weekly hours grid (contract LocationHours): [{ day, windows:[{open,close}] }]. */
  hours: LocationHours;
  /** Whether operating hours have been configured for this location. */
  hoursSet: boolean;
  /** 16:9 hero photo URL. */
  imageUrl: string | undefined;
  /** Product/service kinds offered at this location. */
  products: string[];
  /** The untouched contract row (capacity, rooms[], publicHub, source, …). */
  raw: LocationDto;
}

function composeAddress(d: LocationDto): string {
  const cityRegion = [d.city, [d.region, d.postalCode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return [d.address, cityRegion, d.country].filter(Boolean).join(" · ");
}

/** Map one contract Location → the UI row shape. Pure. */
export function toUILocation(d: LocationDto): UILocation {
  const dd = d as LocationDto & { staffCount?: number; imageUrl?: string; hoursSet?: boolean; products?: string[] };
  return {
    id: d.id,
    name: d.name,
    address: composeAddress(d) || "—",
    city: d.city ?? "",
    tz: d.timezone ?? "",
    status: d.status,
    eventsCount: d.eventsCount,
    roomsCount: d.roomsCount,
    staffCount: dd.staffCount ?? 0,
    capacity: d.capacity ?? undefined,
    publicHub: d.publicHub,
    amenities: d.amenities ?? [],
    active: d.active,
    archived: d.archived,
    hours: ((d as { hours?: LocationHours }).hours) ?? [],
    hoursSet: !!(dd.hoursSet),
    imageUrl: dd.imageUrl ?? undefined,
    products: dd.products ?? [],
    raw: d,
  };
}

/** WeeklyHours record — keyed by day abbreviation, windows use start/end
 *  (matching the editor's native shape). The contract uses open/close. */
export type WeeklyHoursRecord = Partial<Record<string, { start: string; end: string }[]>>;

/**
 * Convert the contract LocationHours array (open/close) → the editor's
 * WeeklyHoursRecord (start/end, keyed by day). This is the ONLY place the
 * shape difference is resolved; the editor and ScheduleGrid never touch raw.
 */
export function hoursToRecord(hours: LocationHours): WeeklyHoursRecord {
  const rec: WeeklyHoursRecord = {};
  for (const item of hours) {
    rec[item.day] = item.windows.map((w) => ({ start: w.open, end: w.close }));
  }
  return rec;
}

/**
 * Convert the editor's WeeklyHoursRecord (start/end) → the contract
 * LocationHours array (open/close). Used when saving.
 */
export function recordToHours(rec: WeeklyHoursRecord): LocationHours {
  return Object.entries(rec)
    .filter(([, wins]) => (wins?.length ?? 0) > 0)
    .map(([day, wins]) => ({
      day,
      windows: (wins ?? []).map((w) => ({ open: w.start, close: w.end })),
    }));
}

const STATUS_META: Record<UILocationStatus, { label: string; labelKey: string; tone: "success" | "neutral" | "warn" }> = {
  active: { label: "Active", labelKey: "common.status.active", tone: "success" },
  inactive: { label: "Inactive", labelKey: "common.status.inactive", tone: "warn" },
  archived: { label: "Archived", labelKey: "locations.adapter.archived", tone: "neutral" },
};
export function locationStatusMeta(s: UILocationStatus) {
  const meta = STATUS_META[s];
  return { ...meta, label: i18nT(meta.labelKey, meta.label) };
}

/**
 * Open-now derivation from the contract `hours` grid + `timezone`. Returns
 * "open" | "closed" | "unknown" ("unknown" = no hours authored ⇒ never assumed
 * 24/7). Mirrors the prototype's locationOpenNow over the now-contracted shape.
 */
export type OpenState = "open" | "closed" | "unknown";
export function locationOpenNow(loc: UILocation): OpenState {
  if (loc.status !== "active") return "closed";
  const hours = loc.hours ?? [];
  if (!hours.length || !loc.tz) return "unknown";
  let day: string, hhmm: string;
  try {
    const tz = loc.tz;
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date());
    const wd = (parts.find((p) => p.type === "weekday")?.value || "").toLowerCase().slice(0, 3);
    const hh = parts.find((p) => p.type === "hour")?.value ?? "00";
    const mm = parts.find((p) => p.type === "minute")?.value ?? "00";
    day = wd; hhmm = `${hh}:${mm}`;
  } catch {
    return "unknown";
  }
  const today = hours.find((h) => h.day === day);
  if (!today || !today.windows.length) return "closed";
  return today.windows.some((w) => w.open <= hhmm && hhmm < w.close) ? "open" : "closed";
}
